import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../header';
import Footer from '../../footer';
import { HiOutlineArrowLeft, HiOutlineShieldCheck, HiOutlineLockClosed } from 'react-icons/hi';

export default function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-[#FAFDFB] min-h-screen text-[#1E293B]">
            <Header />

            <main className="pt-[140px] sm:pt-[170px] pb-[80px] px-[16px] sm:px-[24px] lg:px-[40px] max-w-[1080px] mx-auto">
                {/* Back to Home Link */}
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-[#73C8BD] hover:text-[#5ebcae] font-semibold text-[14px] sm:text-[15px] mb-8 group transition-colors"
                >
                    <HiOutlineArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                    <span>Back to Home</span>
                </Link>

                {/* Page Header */}
                <div className="border-b border-gray-200 pb-8 mb-10">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#EBF7EE] text-[#00B884] text-[13px] font-bold tracking-wide uppercase mb-3">
                        <HiOutlineShieldCheck className="text-[18px]" />
                        <span>Compliance & Data Protection</span>
                    </div>
                    <h1 className="text-[32px] sm:text-[42px] lg:text-[48px] font-extrabold text-[#0F172A] tracking-tight">
                        Privacy Policy
                    </h1>
                    <p className="text-gray-500 text-[14px] sm:text-[15px] mt-2">
                        Last Updated: March 2026 &bull; Rainbow Trucks Private Limited
                    </p>
                </div>

                {/* Content Sections */}
                <div className="prose prose-slate max-w-none space-y-10 text-[15px] sm:text-[16px] leading-relaxed text-[#334155]">
                    
                    <section className="bg-white p-6 sm:p-8 rounded-[20px] border border-gray-100 shadow-sm">
                        <h2 className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] mb-3">
                            1. Introduction & Scope
                        </h2>
                        <p>
                            Rainbow Trucks Private Limited (<strong>"Rainbow Trucks"</strong>, <strong>"Company"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>) respects your privacy and is committed to protecting the personal data of our website visitors, franchise applicants, partners, and customers. This Privacy Policy explains how we collect, use, process, disclose, and safeguard your personal information when you visit our website (<code>rainbowtrucks.com</code>) or communicate with us.
                        </p>
                        <p className="mt-3">
                            This policy is prepared in accordance with the Information Technology Act, 2000, the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011, the Digital Personal Data Protection Act (DPDP Act, 2023), and internationally recognized data privacy principles.
                        </p>
                    </section>

                    <section className="bg-white p-6 sm:p-8 rounded-[20px] border border-gray-100 shadow-sm">
                        <h2 className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] mb-3">
                            2. Information We Collect
                        </h2>
                        <p>We may collect personal and technical information from you in the following ways:</p>
                        <ul className="list-disc pl-6 space-y-2 mt-3 text-gray-700">
                            <li><strong>Franchise Inquiries & Lead Data:</strong> Name, phone number, email address, proposed franchise location/city, investment preference, and any specific inquiries submitted via our contact forms.</li>
                            <li><strong>Customer & Order Inquiries:</strong> Contact information provided when inquiring about bulk kulfi catering, events, or customer feedback.</li>
                            <li><strong>Technical & Browsing Data:</strong> IP address, device type, operating system, browser type, referring URLs, and page navigation patterns collected via standard analytics cookies.</li>
                        </ul>
                    </section>

                    <section className="bg-white p-6 sm:p-8 rounded-[20px] border border-gray-100 shadow-sm">
                        <h2 className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] mb-3">
                            3. Purpose & Legal Basis for Processing
                        </h2>
                        <p>We use the collected information for specific, lawful purposes:</p>
                        <ul className="list-disc pl-6 space-y-2 mt-3 text-gray-700">
                            <li>To respond to franchise inquiries, evaluate candidate applications, and schedule discovery consultations.</li>
                            <li>To coordinate kulfi supplies, cart onboarding, and ongoing franchisee support.</li>
                            <li>To maintain, protect, and optimize our digital website infrastructure.</li>
                            <li>To comply with statutory legal, taxation, and regulatory reporting obligations in India.</li>
                        </ul>
                    </section>

                    <section className="bg-white p-6 sm:p-8 rounded-[20px] border border-gray-100 shadow-sm">
                        <h2 className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] mb-3 flex items-center gap-2">
                            <HiOutlineLockClosed className="text-[#73C8BD]" />
                            <span>4. Data Security & Storage</span>
                        </h2>
                        <p>
                            We implement industry-standard physical, organizational, and technological security controls to protect your data against unauthorized access, alteration, disclosure, or destruction.
                        </p>
                        <p className="mt-3">
                            Our digital data records and website assets are hosted on secure, cloud infrastructure with SSL/TLS 1.3 encryption for data in transit and strong encryption protocols for data at rest.
                        </p>
                    </section>

                    <section className="bg-white p-6 sm:p-8 rounded-[20px] border border-gray-100 shadow-sm">
                        <h2 className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] mb-3">
                            5. Sharing & Disclosure of Information
                        </h2>
                        <p>
                            Rainbow Trucks does not sell, rent, or trade your personal information. We may share data only with:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 mt-3 text-gray-700">
                            <li><strong>Authorized Service Providers:</strong> Trusted cloud hosting, email delivery, and communication partners bound by strict confidentiality agreements.</li>
                            <li><strong>Legal & Regulatory Authorities:</strong> Only when strictly required to comply with applicable laws, court orders, or governmental directives.</li>
                        </ul>
                    </section>

                    <section className="bg-white p-6 sm:p-8 rounded-[20px] border border-gray-100 shadow-sm">
                        <h2 className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] mb-3">
                            6. Your Privacy Rights
                        </h2>
                        <p>
                            Under applicable data protection laws, you have the right to request access to the personal data we hold about you, request corrections to inaccurate information, or request the deletion of your personal data where retention is no longer necessary.
                        </p>
                    </section>

                    <section className="bg-white p-6 sm:p-8 rounded-[20px] border border-gray-100 shadow-sm">
                        <h2 className="text-[20px] sm:text-[22px] font-bold text-[#0F172A] mb-3">
                            7. Grievance Officer & Contact Information
                        </h2>
                        <p>
                            If you have questions, concerns, or requests regarding this Privacy Policy or our data handling practices, please contact our designated Grievance Officer:
                        </p>
                        <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200 text-[14px] leading-relaxed">
                            <p><strong>Grievance Officer:</strong> Rainbow Trucks Private Limited</p>
                            <p><strong>Registered Address:</strong> 10th Street, Ram Nagar, Puzhuthivakkam, Madipakkam, Chennai, Tamil Nadu 600091, India</p>
                            <p><strong>Official Email:</strong> <a href="mailto:hello@rainbowtrucks.com" className="text-[#73C8BD] font-semibold underline">hello@rainbowtrucks.com</a></p>
                            <p><strong>Direct Helpline:</strong> +91 99399 35343</p>
                        </div>
                    </section>

                </div>
            </main>

            <Footer />
        </div>
    );
}
